import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface ThemeContextType {
  theme: 'light' | 'dark'
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// A manually-saved preference wins over Telegram's color scheme; when the user
// hasn't chosen one, we follow the Telegram client's theme (light/dark).
const MANUAL_THEME_KEY = 'theme'

function getTelegramColorScheme(): 'light' | 'dark' | null {
  const scheme = window.Telegram?.WebApp?.colorScheme
  return scheme === 'dark' || scheme === 'light' ? scheme : null
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Prefer an explicit user choice; otherwise mirror the Telegram client theme,
  // finally falling back to light.
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem(MANUAL_THEME_KEY) as 'light' | 'dark' | null
    return savedTheme || getTelegramColorScheme() || 'light'
  })

  // Follow Telegram theme changes unless the user has picked a theme manually.
  useEffect(() => {
    const tg = window.Telegram?.WebApp
    if (!tg?.onEvent) return

    const handleThemeChange = () => {
      if (localStorage.getItem(MANUAL_THEME_KEY)) return // respect manual choice
      const scheme = getTelegramColorScheme()
      if (scheme) setTheme(scheme)
    }

    tg.onEvent('themeChanged', handleThemeChange)
    return () => tg.offEvent?.('themeChanged', handleThemeChange)
  }, [])

  // Apply theme class whenever theme changes (no persistence here — persistence
  // only happens on an explicit toggle so auto-following isn't sticky).
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark-theme')
    } else {
      document.documentElement.classList.remove('dark-theme')
    }
  }, [theme])

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'light' ? 'dark' : 'light'

      // Persist ONLY on explicit toggle so it overrides Telegram auto-theming.
      localStorage.setItem(MANUAL_THEME_KEY, newTheme)

      // Haptic feedback if available
      const tg = window.Telegram?.WebApp
      if (tg?.HapticFeedback) {
        try {
          tg.HapticFeedback.impactOccurred('light')
        } catch {
          // Ignore haptic errors
        }
      }

      return newTheme
    })
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}